import React, { useEffect, useState } from "react";
import {
  FiBarChart2,
  FiList,
  FiMessageCircle,
  FiPieChart,
  FiUsers,
  FiX,
  FiMenu,
  FiChevronLeft,
  FiMessageSquare,
  FiFileText,
} from "react-icons/fi";
import { Avatar, Button, Divider } from "@heroui/react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRightEndOnRectangleIcon } from "@heroicons/react/24/outline";
import ConfirmationModal from "@/components/modal/ConfirmationModal";
import { TbBrandTrello } from "react-icons/tb";
import { useSidebarStore } from "@/store/sidebarStore";
import clsx from "clsx";

const AdminSidebar = () => {
  const { logout, currentUser } = useAuth();
  const location = useLocation();
  const { isOpen, toggle } = useSidebarStore();

  const [isLogoutOpen, setIsLogoutOpen] = useState(false);

  const sidebarLinks = [
    { id: 1, name: "Dashboard", path: "/dashboard", icon: <FiBarChart2 /> },
    {
      id: 3,
      name: "Cohorts",
      path: "/active-cohorts",
      icon: <FiPieChart />,
    },
    {
      id: 4,
      name: "Clients",
      path: "/client-profiles",
      icon: <TbBrandTrello />,
    },
    {
      id: 5,
      name: "Trainers",
      path: "/user-management",
      icon: <FiUsers />,
    },
    {
      id: 6,
      name: "Status",
      path: "/admin/payment-status",
      icon: <FiList />,
    },
    {
      id: 7,
      name: "Transactions",
      path: "/admin/transaction-groups",
      icon: <FiMessageSquare />,
    },
    {
      id: 8,
      name: "Templates",
      path: "/assessment-templates",
      icon: <FiMessageCircle />,
    },
    {
      id: 9,
      name: "Reports",
      path: "/reports",
      icon: <FiFileText />,
    },
  ];

  return (
    <>
      <div
        className={clsx(
          "fixed top-0 z-40 flex flex-col justify-between h-full shadow-md px-6 py-4 xl:py-6 overflow-y-auto transition-all duration-300 ease-in-out bg-white",
          isOpen ? "w-[320px]" : "w-[80px]"
        )}
      >
        <div>
          <div className="flex justify-between items-center">
            {isOpen && (
              <img
                src="/assets/images/logo/logo.png"
                alt="Logo"
                className="h-9 xl:h-11 w-auto mb-2 xl:mb-4"
              />
            )}
            <button
              onClick={toggle}
              className={clsx(
                "text-gray-500 hover:text-gray-700 p-1 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300",
                !isOpen && "mx-auto mb-4"
              )}
            >
              {isOpen ? <FiChevronLeft size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
          <Divider className="h-0.5 bg-gray-200" />
          {isOpen && (
            <div className="my-2 xl:my-4 text-lg xl:text-xl font-bold leading-none text-gray-800 p-2">
              Welcome, {currentUser?.name || "User"}
            </div>
          )}
          <Divider className="h-0.5 bg-gray-200" />
          <div className="flex flex-col gap-1 py-3 xl:gap-2 xl:py-4">
            {sidebarLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.id}
                  to={link.path}
                  className={clsx(
                    "flex items-center px-4 py-2 xl:py-3 rounded-xl hover:bg-blueLight-100 hover:text-blueDark",
                    isActive
                      ? "bg-blueLight text-blueDark"
                      : "text-gray-900 hover:bg-gray-200",
                    !isOpen && "justify-center"
                  )}
                  title={!isOpen ? link.name : undefined}
                >
                  <div
                    className={clsx(
                      "text-xl xl:text-2xl",
                      isActive ? "text-blueDark" : "text-gray-400",
                      isOpen ? "mr-3" : "mr-0"
                    )}
                  >
                    {link.icon}
                  </div>
                  {isOpen && link.name}
                </Link>
              );
            })}
          </div>
        </div>
        <div>
          <Divider className="bg-gray-200 h-0.5 my-4" />
          <div
            className={clsx(
              "flex gap-2 items-center pl-2 mt-4 w-full",
              isOpen ? "justify-between" : "justify-center"
            )}
          >
            {isOpen ? (
              <>
                <div className="flex gap-3 items-center self-stretch my-auto font-bold text-gray-900 min-w-0">
                  <Avatar
                    name="JG"
                    className="bg-blueLight text-blueDark font-medium shrink-0"
                  />
                  <span className="self-stretch my-auto text-gray-900 text-xs truncate max-w-[80px] xl:max-w-[100px]">
                    {currentUser?.name}
                  </span>
                </div>
                <Button
                  className="w-auto !p-0 flex items-center gap-3 bg-transparent hover:border-transparent border-transparent text-base !text-grayColor font-medium shrink-0"
                  onPress={() => setIsLogoutOpen(true)}
                >
                  Logout
                  <ArrowRightEndOnRectangleIcon className="w-8 h-8 font-bold text-grayColor" />
                </Button>
              </>
            ) : (
              <Button
                className="w-auto !p-2 bg-transparent hover:border-transparent border-transparent"
                onPress={() => setIsLogoutOpen(true)}
                title="Logout"
              >
                <ArrowRightEndOnRectangleIcon className="w-6 h-6 font-bold text-grayColor" />
              </Button>
            )}
          </div>
        </div>
      </div>
      <ConfirmationModal isOpen={isLogoutOpen} onOpenChange={setIsLogoutOpen}>
        <div className="text-lg font-semibold mb-3">
          Are you Sure want to Logout?
        </div>
        <div className="flex justify-end gap-2">
          <Button onPress={() => setIsLogoutOpen(false)}>No</Button>
          <Button
            variant="solid"
            className="bg-grayColor text-white"
            onPress={() => logout()}
          >
            Yes
          </Button>
        </div>
      </ConfirmationModal>
    </>
  );
};

export default AdminSidebar;
